import { useMutation, useQueryClient } from 'react-query';

import { CreateDocChronologyRequestDto } from '../../../../backend/src/matter/subset/chronology/doc-chronology/dto/create-doc-chron-request.dto';
import { CreateDocChronologyResponseDto } from '../../../../backend/src/matter/subset/chronology/doc-chronology/dto/create-doc-chron-response.dto';
import { TrackingEventsV2 } from '../../analytics/eventsV2';
import { trackCustomEvent } from '../../analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface CreateDocChronologyParameters extends CreateDocChronologyRequestDto {
  matterId: string;
  subsetId: string;
}

interface HookProps {
  onSuccess?: (data: CreateDocChronologyResponseDto) => void;
  onError?: (error: Error) => void;
}

export const useCreateDocChronology = ({ onSuccess, onError }: HookProps = {}) => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      matterId,
      subsetId,
      docIds,
      includeKeyFacts,
      includeRelevantFacts,
      includeAllFacts,
    }: CreateDocChronologyParameters) => {
      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          docIds,
          includeKeyFacts,
          includeRelevantFacts,
          includeAllFacts,
        } satisfies CreateDocChronologyRequestDto,
      });

      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology`,
        fetchConfig,
      );

      if (!res.ok) {
        throw new Error('Network response was not ok');
      }

      return res.json() as Promise<CreateDocChronologyResponseDto>;
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([QueryEntity.DocChronologies, { subsetId: variables.subsetId }]);
        trackCustomEvent(TrackingEventsV2.DocChronologyCreate);
        onSuccess?.(data);
      },
      onError: (error: Error) => {
        onError?.(error);
      },
    },
  );
};
